import { Component, OnInit } from '@angular/core';
import * as moment from 'moment';

import { ModalService } from '../../../shared/modules/modal';
import {
  UncontrolledTransactionsService,
  TransactionsService,
  UncontrolledEntitiesService,
  EntitiesService,
  PartyRolesService,
  SessionService,
  UtilsService,
  NotificationService,
  NumberFormatService
} from '../../../shared/services';

import { Dates, TinyTextEditor, TranslationConstants, UncontrolledTransactionConstants } from '../../../shared/constants';

import {
  ConfirmationModalComponent
} from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  ClosableModalWithErrorResponseModel
} from '../../../shared/components/base/closable-modal-with-error-response-model';

import {
  Entity,
  UncontrolledEntity,
  TransactionType,
  PartyRole,
  UncontrolledTransaction,
  Container,
  Currency,
  UncontrolledTransactionValidationErrorResponse,
  Message,
  TransactionValueEnteredInEnum,
  Country,
  TransactionCurrencyDropdownValue
} from '../../../shared/models';

@Component({
  selector: 'uncontrolled-transaction-modal',
  templateUrl: './uncontrolled-transaction-modal.component.html',
  styleUrls: ['./uncontrolled-transaction-modal.component.scss']
})
export class UncontrolledTransactionModalComponent
  extends ClosableModalWithErrorResponseModel<UncontrolledTransactionValidationErrorResponse>
  implements OnInit {
  transaction: UncontrolledTransaction;
  ucEntities: UncontrolledEntity[] = [];
  entities: Entity[] = [];
  sourceEntityId: number;
  sourceUcEntityId: number;
  destinationEntityId: number;
  destinationUcEntityId: number;
  sourceEntity: Entity;
  destinationEntity: Entity;
  transactionTypes: TransactionType[] = [];
  partyRoles: PartyRole[] = [];
  workingContainer: Container;
  upeCurrency: Currency;
  upeCurrencyScale: number;
  data: number;
  valueInputTouched = false;
  unitsInputTouched = false;
  descriptionInputTouched = false;
  transferDateYears = [];
  transferDateMonths = [];
  transferDateDays = [];
  taxYear: number;
  valueContainsMinus = false;
  unitsContainsMinus = false;
  inputsOldValue = {
    units: '',
    value: '',
  };
  lockedFields: string[] = [];
  transactionCurrencyDropdownValues: TransactionCurrencyDropdownValue[] = [];
  countries: Country[] = [];

  tinyInit = TinyTextEditor.INIT;

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _ucTransactionsService: UncontrolledTransactionsService,
    private _transactionsService: TransactionsService,
    private _ucEntitiesService: UncontrolledEntitiesService,
    private _entitiesService: EntitiesService,
    private _partyRolesService: PartyRolesService,
    private _sessionService: SessionService,
    private _utilsService: UtilsService,
    private _numberFormatService: NumberFormatService
  ) {
    super(notificationService, modalService);
    this.validationErrors = new UncontrolledTransactionValidationErrorResponse();
    this.workingContainer = this._sessionService.getWorkingContainer();
  }

  ngOnInit(): void {
    this.upeCurrency = this._sessionService.getContainerUpeCurrency();
    this.upeCurrencyScale = this._sessionService.getContainerUpeCurrencyScale();
    this.transaction = new UncontrolledTransaction();

    this.getAllEntities();

    this.getAllTransactionTypes();
    this.getAllPartyRoles();
    this.getCountries();
    this.populateTransferDates();
    this.setTransactionCurrencyDropdownValues();
    this.listenForErrorMessages();

    if (this.data) {
      this.setTransactionData(this.data);
    }
  }

  close(form = null): void {
    if (form && form.form.touched ||
      (this.unitsInputTouched || this.valueInputTouched || this.descriptionInputTouched)) {
      this.modalService.openModal({
        component: ConfirmationModalComponent,
        data: {
          title: TranslationConstants.TRANSACTION_CLOSE_TITLE,
          content: TranslationConstants.GLOBAL_CANCEL_MODAL_MESSAGE,
        } as Message,
        success: () => {
          this.closeAllModals();
        },
      });
    } else {
      this.closeAllModals();
    }
  }

  saveAndNew(): void {
    const model = this.prepareModelForSaving();
    this.responseErrors = [];
    this.validationErrors = new UncontrolledTransactionValidationErrorResponse();
    this._ucTransactionsService.save(model).subscribe(() => {
      this.close();
    });
  }

  save(form, closeModal = false): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    this.responseErrors = [];
    this.validationErrors = new UncontrolledTransactionValidationErrorResponse();
    const preparedModel = this.prepareModelForSaving();
    if (!this.data) {
      this.saveNew(preparedModel, closeModal, form);
    } else {
      this.edit(preparedModel);
    }
  }

  getFormated(num, maxFractionDigits = 1): string {
    return this._numberFormatService.getFormatedNumber(num, maxFractionDigits);
  }

  setOldValue(event, field) {
    this.inputsOldValue[field] = event.target.value;
  }

  formatNumberInput(event, element, field, maxFractionDigits = 1): void {
    const validInputRegex = /^[-]?[0-9,]*[.]?[0-9]*$/g;
    const firstChar = event.target.value.slice(0, 1);
    const startsWithDotOrComma = firstChar.includes('.') || firstChar.includes(',');
    const startsWithMinus = firstChar === '-';

    if (!validInputRegex.test(event.target.value)) {
      event.target.value = this.inputsOldValue[field];

      return;
    }

    this.valueContainsMinus = field === 'value' ? event.target.value.includes('-') : this.valueContainsMinus;
    this.unitsContainsMinus = field === 'units' ? event.target.value.includes('-') : this.unitsContainsMinus;

    // reset backend error message on change
    this.validationErrors[field] = '';

    if (field === 'value') {
      this.setValueInputTouchedState();
    }

    this.transaction[field] = this._numberFormatService.handleCursorPosition(
      event,
      element,
      this.transaction[field],
      maxFractionDigits,
      startsWithDotOrComma,
      startsWithMinus
    );

    if (field === 'localValue') {
      this.setLocalValueIfNeeded();
    }
  }

  setValueInputTouchedState(touched = true): void {
    this.valueInputTouched = touched;
  }

  setUnitsInputTouchedState(touched = true): void {
    this.unitsInputTouched = touched;
  }

  /**
   * nedded beacuse if user goes directly from description to close
   * it does not recognize that it was touched
   */
  setDescriptionInputTouchedState(touched = true): void {
    this.descriptionInputTouched = touched;
  }

  shouldShowValueErrorMessage(): boolean {
    return !this.valueContainsMinus &&
      (!this.transaction.value && this.valueInputTouched);
  }

  onMonthChange(): void {
    const month = Number(
      moment().month(this.transaction.transferDateMonth).format('MM')
    );
    const yearAndMonth = `${this.taxYear}-${month}`;

    this.transferDateDays = [];
    for (let day = 1; day < moment(yearAndMonth).daysInMonth() + 1; day++) {
      this.transferDateDays.push(day);
    }
  }

  resetDate(): void {
    this.transferDateDays = [];
    this.transaction.transferDateDay = '';
    this.transaction.transferDateMonth = '';
    this.transaction.transferDateYear = '';
  }

  checkForValidationError(code): boolean {
    return !!this.validationErrors[code];
  }

  getCurrencyScaleString(currencyScale?: number): string {
    return this._utilsService.getCurrencyScaleString(currencyScale || this.upeCurrencyScale);
  }

  getLocalCurrencyScaleString(): string {
    const localCurrencyScale = this.getLocalCurrencyScale();

    return this.getCurrencyScaleString(localCurrencyScale);
  }

  entitySelected(entityId, uncontrolled: boolean, source: boolean): void {
    switch (source + '|' + uncontrolled) {
      case 'true|true':
        this.sourceEntityId = null;
        this.sourceUcEntityId = entityId;
        break;
      case 'true|false':
        this.sourceEntityId = entityId;
        this.sourceUcEntityId = undefined;
        break;
      case 'false|true':
        this.destinationEntityId = null;
        this.destinationUcEntityId = entityId;
        break;
      case 'false|false':
        this.destinationEntityId = entityId;
        this.destinationUcEntityId = undefined;
        break;
      default:
        break;
    }

    this.setTransactionCurrencyDropdownValues();
    this.setSourceAndDestinationEntity();
    this.setLocalValueIfNeeded();
  }

  isMneType(): boolean {
    return this.transaction.valueEnteredIn === TransactionValueEnteredInEnum.MNE;
  }

  getCurrency(valueEnteredIn: TransactionValueEnteredInEnum): Currency {
    if (valueEnteredIn === TransactionValueEnteredInEnum.Source) {
      return this.countries.find(country => country.isoCode === this.sourceEntity.domicile.isoCode) ? this.countries.find(country => country.isoCode === this.sourceEntity.domicile.isoCode).defaultCurrency : null;
    }

    if (valueEnteredIn === TransactionValueEnteredInEnum.Destination) {
      return this.countries.find(country => country.isoCode === this.destinationEntity.domicile.isoCode) ? this.countries.find(country => country.isoCode === this.destinationEntity.domicile.isoCode).defaultCurrency : null;
    }

    return this.upeCurrency;
  }

  getCurrencyIsoCode(): string {
    return this.transactionCurrencyDropdownValues.find(transactionCurrencyDropdownValue => transactionCurrencyDropdownValue.value === this.transaction.valueEnteredIn).currency.isoCode;
  }

  getLocalCurrencyScale(): number {
    if (this.transaction.valueEnteredIn === TransactionValueEnteredInEnum.Source) {
      return this.getCurrencyConversionAttribute('localCurrencyScale');
    }

    if (this.transaction.valueEnteredIn === TransactionValueEnteredInEnum.Destination) {
      return this.getCurrencyConversionAttribute('localCurrencyScale', this.destinationEntity);
    }
  }

  shouldShowNoCurrencyConversionErrorMessage(): boolean {
    if (this.transaction.valueEnteredIn !== null && this.transaction.valueEnteredIn !== undefined && !this.isMneType()) {
      return !this.getSourceOrDestinationConversionValue();
    }

    return false;
  }

  setLocalValueIfNeeded(): void {
    if (
      (this.isMneType() && !this.transaction.value) ||
      (!this.isMneType() && !this.transaction.value && !this.transaction.localValue)
    ) {

      // don't calculate anything if there are no values needed for calculations
      return;
    }

    const conversionValue = this.getSourceOrDestinationConversionValue();

    if (!this.isMneType()) {
      if ((this.transaction.localValue === null || this.transaction.localValue === undefined) && !this.transaction.value) {
        this.transaction.localValue = '';
        this.transaction.value = 0;
      } else if (this.shouldCalculateLocalValue()) {
        const localCurrencyScale = this.getLocalCurrencyScale();

        this.transaction.localValue = this._numberFormatService.getFormatedNumber(
          this._utilsService.calcToLocalCurrency(
            this.transaction.value,
            this.upeCurrencyScale,
            this._numberFormatService.removeCommaAndGetNumber(conversionValue, 1),
            localCurrencyScale
          ),
          6
        );
      } else {
        this.calculateValue(conversionValue);
      }
    } else {
      // set local value to '' because it's hidden in this case, it has to be recalculated for next appearance
      this.transaction.localValue = '';
    }
  }

  private getSourceOrDestinationConversionValue(): number {
    if (this.transaction.valueEnteredIn === TransactionValueEnteredInEnum.Source) {
      return this.getCurrencyConversionAttribute('conversionValue');
    }

    if (this.transaction.valueEnteredIn === TransactionValueEnteredInEnum.Destination) {
      return this.getCurrencyConversionAttribute('conversionValue', this.destinationEntity);
    }
  }

  private getCurrencyConversionAttribute(
    attribute: string,
    sourceOrDestinationEntity: Entity = this.sourceEntity
  ): number {
    if (sourceOrDestinationEntity) {
      const currencyConversion = sourceOrDestinationEntity.legalEntityCurrencyConversion.find(legalEntityCurrencyConversion => legalEntityCurrencyConversion.taxYear === this.workingContainer.taxYear);
      return currencyConversion ? currencyConversion[attribute] : null;
    }
    return 1;
  }

  private shouldCalculateLocalValue(): boolean {
    return !!this.transaction.value && !this.transaction.localValue;
  }

  private calculateValue(conversionValue: number): void {
    if (this.shouldShowNoCurrencyConversionErrorMessage()) {
      this.transaction.value = this._numberFormatService.removeCommaAndGetNumber(this.transaction.localValue, 0);
      this.transaction.localValue = '';
      return;
    }

    if (!this.transaction.localValue || !conversionValue) {
      return;
    }

    const localValue = this.getValueBasedOnType(this.transaction.localValue);
    const localCurrencyScale = this.getLocalCurrencyScale();

    this.transaction.value = this._utilsService.calcToMneCurrency(
      localValue,
      this.upeCurrencyScale,
      this._numberFormatService.removeCommaAndGetNumber(conversionValue, 1),
      localCurrencyScale
    );
  }

  private getValueBasedOnType(localValue: string): number {
    // TODO consider moving this logic to removeCommaAndGetNumber function
    return typeof localValue === 'string' ?
      this._numberFormatService.removeCommaAndGetNumber(localValue, 0) :
      localValue;
  }

  private setSourceAndDestinationEntity(): void {
    if (this.sourceEntityId) {
      this.sourceEntity = this.entities.find(entity => entity.entityId === this.sourceEntityId);

      this.transactionCurrencyDropdownValues.push({
        key: TransactionValueEnteredInEnum[TransactionValueEnteredInEnum.Source],
        value: TransactionValueEnteredInEnum.Source,
        currency: this.getCurrency(TransactionValueEnteredInEnum.Source),
      });
    }

    if (this.destinationEntityId) {
      this.destinationEntity = this.entities.find(entity => entity.entityId === this.destinationEntityId);

      this.transactionCurrencyDropdownValues.push({
        key: TransactionValueEnteredInEnum[TransactionValueEnteredInEnum.Destination],
        value: TransactionValueEnteredInEnum.Destination,
        currency: this.getCurrency(TransactionValueEnteredInEnum.Destination),
      });
    }
  }

  private saveNew(preparedModel: UncontrolledTransaction, closeModal, form): void {
    this._ucTransactionsService.save(preparedModel)
      .subscribe(() => {
        this.success();
        if (closeModal) {
          this.closeAllModals();
        }
        this.isSaveDisabled = false;
        this.resetForm(form);
      });
  }

  private edit(preparedModel: UncontrolledTransaction): void {
    this._ucTransactionsService.edit(preparedModel.transactionId, preparedModel)
      .subscribe(() => {
        this.success();
        this.closeAllModals();
      });
  }

  private resetForm(form): void {
    this.transaction = new UncontrolledTransaction();
    form.reset();
    this.responseErrors = [];
    this.setValueInputTouchedState(false);
    this.setUnitsInputTouchedState(false);
    this.setDescriptionInputTouchedState(false);
    this.resetDate();
    this.validationErrors = new UncontrolledTransactionValidationErrorResponse();
    this.setTransactionCurrencyDropdownValues();
  }

  private getAllEntities(): void {
    const queryParam = `?sort=code&order=asc`;
    this._ucEntitiesService.list(queryParam).subscribe(({ result }) => {
      this.ucEntities = result.uncontrolledEntities;
    });

    this._entitiesService.list(queryParam).subscribe(({ data }) => {
      this.entities = data;
    });
  }

  private getAllTransactionTypes(): void {
    this._transactionsService.getAllTransactionTypes().subscribe(({ result }) => {
      this.transactionTypes = result.transactionTypes;
    });
  }

  private getAllPartyRoles(): void {
    this._partyRolesService.list().subscribe(({ data }) => {
      this.partyRoles = data;
    });
  }

  private prepareModelForSaving(): UncontrolledTransaction {
    const model = JSON.parse(JSON.stringify(this.transaction));

    if (model.value === undefined) {
      model.value = null;
    }

    model.transferDateMonth = Number(
      moment().month(model.transferDateMonth).format('MM')
    );
    model.transferDateDay = Number(model.transferDateDay);
    model.transferDateYear = Number(model.transferDateYear);

    if (this.transaction.transferDateMonth === '') {
      delete model.transferDateMonth;
      delete model.transferDateDay;
    }

    if (this.transaction.transferDateDay === '') {
      delete model.transferDateDay;
    }

    if (!this.transaction.sourceLegalEntity.entityId) {
      model.sourceLegalEntity = null;
    }

    if (!this.transaction.destinationLegalEntity.entityId) {
      model.destinationLegalEntity = null;
    }

    if (!this.transaction.ucSourceLegalEntity.entityId) {
      model.ucSourceLegalEntity = null;
    }

    if (!this.transaction.ucDestinationLegalEntity.entityId) {
      model.ucDestinationLegalEntity = null;
    }

    if (!this.isMneType()) {
      model.value = this.getValueBasedOnType(model.localValue);
      delete model.localValue;
    }

    return model;
  }

  private setTransactionData(transactionId: number): void {
    this._ucTransactionsService.show(transactionId).subscribe(({ result }) => {
      this.transaction = result.transaction;
      this.lockedFields = result.locked;

      this.transaction.ucSourceLegalEntity = this.transaction.ucSourceLegalEntity || new UncontrolledEntity();
      this.transaction.ucDestinationLegalEntity = this.transaction.ucDestinationLegalEntity ||
        new UncontrolledEntity();

      this.transaction.sourceLegalEntity = this.transaction.sourceLegalEntity || new Entity();
      this.transaction.destinationLegalEntity = this.transaction.destinationLegalEntity || new Entity();
      this.sourceEntityId = this.transaction.sourceLegalEntity.entityId;
      this.sourceUcEntityId = this.transaction.ucSourceLegalEntity.entityId;
      this.destinationEntityId = this.transaction.destinationLegalEntity.entityId;
      this.destinationUcEntityId = this.transaction.ucDestinationLegalEntity.entityId;
      this.transaction.transferDateMonth = this.transaction.transferDateMonth ?
        moment(`${this.transaction.transferDateMonth}`).format('MMM') : '';

      this.transaction.transferDateDay = this.transaction.transferDateDay || '';

      this.setSourceAndDestinationEntity();
      this.onMonthChange();
      this.setLocalValueIfNeeded();
    });
  }

  private populateTransferDates(): void {
    this.taxYear = this.workingContainer.taxYear;
    const numberOfYearsToDisplay = this.taxYear + 2 - UncontrolledTransactionConstants.MIN_UNCONTROLLED_TRANSACTION_YEAR;

    this.transferDateYears = Array(numberOfYearsToDisplay).fill(0).map((_, idx) => this.taxYear + 1 - idx);

    this.transferDateMonths = Dates.MONTHS;
  }

  private setTransactionCurrencyDropdownValues(): void {
    this.transactionCurrencyDropdownValues = [
      {
        value: TransactionValueEnteredInEnum.MNE,
        key: TransactionValueEnteredInEnum[TransactionValueEnteredInEnum.MNE],
        currency: null, // will be added in the addCurrencyProps function
      },
    ];
    this.addCurrencyProps();
  }

  private addCurrencyProps(): void {
    this.transactionCurrencyDropdownValues = this.transactionCurrencyDropdownValues.map((currencyType) => {
      currencyType.currency = this.getCurrency(currencyType.value);

      return currencyType;
    });
  }

  private getCountries(): void {
    this.countries = this._sessionService.getCountries();
    this.addCurrencyProps();
  }
}
